import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import SpotMap from "@/components/SpotMap";
import SpotCard from "@/components/SpotCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal, MapPinned, X, Heart } from "lucide-react";
import { useFavorites } from "@/lib/favorites";

const ALL = "__all__";

export default function SpotsPage() {
  const [spots, setSpots] = useState([]);
  const [filters, setFilters] = useState({ regions: [], water_types: [], fish: [] });
  const [region, setRegion] = useState(ALL);
  const [water, setWater] = useState(ALL);
  const [fish, setFish] = useState(ALL);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api.get("/filters").then((r) => setFilters(r.data));
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (region !== ALL) params.region = region;
    if (water !== ALL) params.water_type = water;
    if (fish !== ALL) params.fish = fish;
    if (search) params.search = search;
    api.get("/spots", { params }).then((r) => setSpots(r.data));
  }, [region, water, fish, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const { favs } = useFavorites();
  const [onlyFavs, setOnlyFavs] = useState(false);
  const shown = onlyFavs ? spots.filter((s) => favs.includes(s.id)) : spots;

  const reset = () => { setRegion(ALL); setWater(ALL); setFish(ALL); setSearch(""); setOnlyFavs(false); };
  const hasFilter = region !== ALL || water !== ALL || fish !== ALL || search || onlyFavs;

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="grid lg:grid-cols-[440px_1fr] min-h-[calc(100vh-4rem)]">
        {/* LEFT PANEL */}
        <div className="lg:h-[calc(100vh-4rem)] lg:overflow-y-auto border-r border-emerald-900/10 bg-background">
          <div className="p-6 sm:p-8">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2">
              <MapPinned className="w-4 h-4" /> Carte des spots
            </p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-emerald-950 mt-1">
              {shown.length} spot{shown.length > 1 ? "s" : ""} de pêche
            </h1>

            {/* filters */}
            <div className="mt-5 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                <Input
                  data-testid="spot-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un lieu, une région…"
                  className="pl-9 rounded-xl border-emerald-900/15 bg-white"
                />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="filter-region" className="rounded-xl bg-white border-emerald-900/15">
                    <SelectValue placeholder="Région" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Toutes les régions</SelectItem>
                    {filters.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-2 gap-3">
                  <Select value={water} onValueChange={setWater}>
                    <SelectTrigger data-testid="filter-water" className="rounded-xl bg-white border-emerald-900/15">
                      <SelectValue placeholder="Type d'eau" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>Toutes eaux</SelectItem>
                      {filters.water_types.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={fish} onValueChange={setFish}>
                    <SelectTrigger data-testid="filter-fish" className="rounded-xl bg-white border-emerald-900/15">
                      <SelectValue placeholder="Poisson" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>Tous poissons</SelectItem>
                      {filters.fish.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setOnlyFavs(!onlyFavs)}
                data-testid="filter-favorites"
                aria-pressed={onlyFavs}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  onlyFavs ? "bg-rose-500 text-white border-rose-500" : "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
                }`}
              >
                <Heart className={`w-4 h-4 ${onlyFavs ? "fill-white" : ""}`} /> Mes favoris ({favs.length})
              </button>

              {hasFilter && (
                <Button
                  variant="ghost"
                  onClick={reset}
                  data-testid="reset-filters"
                  className="text-emerald-700 hover:text-emerald-900 gap-1 h-8 px-2"
                >
                  <X className="w-4 h-4" /> Réinitialiser
                </Button>
              )}
            </div>

            {/* list */}
            <div className="mt-6 space-y-4">
              {shown.length === 0 && (
                <div className="text-center py-12 text-muted-foreground" data-testid="no-spots">
                  <SlidersHorizontal className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
                  {onlyFavs ? "Aucun favori pour l'instant. Touchez le cœur d'un spot pour l'ajouter." : "Aucun spot ne correspond à ces filtres."}
                </div>
              )}
              {shown.map((s) => (
                <SpotCard key={s.id} spot={s} onHover={setActiveId} active={activeId === s.id} />
              ))}
            </div>
          </div>
        </div>

        {/* MAP */}
        <div className="relative h-[60vh] lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16 p-4">
          <SpotMap spots={shown} activeId={activeId} onSelect={setActiveId} />
        </div>
      </div>
    </div>
  );
}
